import { gql } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import {
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuContent,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import classnames from 'classnames';
import { LocationDescriptor } from 'history';
import naturalCompare from 'natural-compare-lite';
import React, { useMemo } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { GraphRef, graphRefToString } from 'src/app/graph/hooks/useGraphRef';
import { useGraphVisibility } from 'src/app/graph/hooks/useGraphVisibility';
import {
  checkRouteConfig,
  launchesRouteConfig,
  recentChecksRouteConfig,
  variantPersistedQueriesRouteConfig,
  variantProposalListRouteConfig,
} from 'src/app/graph/routes';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { GraphType } from 'src/lib/graphqlTypes/types';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

export const GraphVariantSwitcherFragment = gql`
  fragment GraphVariantSwitcherFragment on Account {
    id
    name
    services {
      id
      title
      graphType
      variants {
        id
        name
        isFavoriteOfCurrentUser
        isContract
        permissions {
          canQueryBuildConfig
        }
      }
    }
  }
`;

interface Props {
  graphs: GraphQLTypes.GraphVariantSwitcherFragment['services'];
  currentGraphRef: GraphRef;
  currentGraphTitle: string;
  graphVisibilityType: ReturnType<typeof useGraphVisibility>;
}

interface GraphOptionData {
  graph: GraphQLTypes.GraphVariantSwitcherFragment['services'][number];
  variant: GraphQLTypes.GraphVariantSwitcherFragment['services'][number]['variants'][number];
}

export const GraphVariantSwitcher = ({
  graphs,
  currentGraphRef,
  currentGraphTitle,
  graphVisibilityType,
}: Props) => {
  const location = useLocation();
  const { variantOptions, keyedVariants, hasClassicGraphs } = useMemo(() => {
    const groupedGraphs: {
      favorites: GraphOptionData[];
      current: GraphOptionData[];
      rest: Record<string, GraphOptionData[]>;
      all: Record<string, GraphOptionData>;
    } = { favorites: [], current: [], rest: {}, all: {} };
    let foundClassicGraphs = false;
    graphs.forEach((graph) => {
      return [...graph.variants]
        .sort((a, b) => naturalCompare(a.name, b.name))
        .forEach((variant) => {
          const value = { graph, variant };
          foundClassicGraphs =
            foundClassicGraphs || graph.graphType === GraphType.CLASSIC;
          groupedGraphs.all[
            graphRefToString({
              graphId: graph.id,
              graphVariant: variant.name,
            })
          ] = value;
          if (variant.isFavoriteOfCurrentUser) {
            groupedGraphs.favorites.push(value);
          } else if (graph.id === currentGraphRef.graphId) {
            groupedGraphs.current.push(value);
          } else {
            getOrSetRecord(graph.id, [], groupedGraphs.rest).push(value);
          }
        });
    });

    return {
      hasClassicGraphs: foundClassicGraphs,
      variantOptions: (
        [
          [{ label: 'Favorites' }, groupedGraphs.favorites],
          [
            { label: `${currentGraphTitle} (Current Graph)` },
            groupedGraphs.current,
          ],
          ...Object.entries(groupedGraphs.rest),
        ] as const
      ).filter(([, g]) => g.length),
      keyedVariants: groupedGraphs.all,
    };
  }, [currentGraphRef.graphId, graphs, currentGraphTitle]);

  const graphRefValue = graphRefToString(currentGraphRef);
  const currentGraph = keyedVariants[graphRefValue];
  return Object.keys(keyedVariants).length > 0 ? (
    <Menu isLazy placement="bottom-start">
      <MenuButton
        as={Button}
        variant="hidden"
        size="sm"
        value={graphRefValue}
        rightIcon={<IconChevronDown />}
      >
        {!currentGraph ? (
          'Select a graph / variant'
        ) : (
          <>{`${currentGraph?.graph.title} / ${currentGraph?.variant.name}`}</>
        )}
      </MenuButton>
      <MenuContent>
        <MenuList>
          {variantOptions.map(([group, variants], idx) => {
            const { title, graphType } = variants[0]?.graph ?? {};
            const label =
              (typeof group === 'string' ? title : group.label) || '';
            return (
              <React.Fragment
                key={typeof group === 'string' ? group : group.label}
              >
                <MenuGroup
                  label={
                    graphType &&
                    [
                      GraphType.CLOUD_SUPERGRAPH,
                      GraphType.SELF_HOSTED_SUPERGRAPH,
                    ].includes(graphType) &&
                    hasClassicGraphs &&
                    (typeof group === 'string' ||
                      group.label !== 'Favorites') ? (
                      <span className="flex w-full items-center justify-between">
                        <span className="flex-1 truncate">{label} </span>
                        <Badge
                          variant="neutralOutlined"
                          className="ml-2 flex-none"
                        >
                          SUPERGRAPH
                        </Badge>
                      </span>
                    ) : (
                      label
                    )
                  }
                >
                  {variants.map(({ graph, variant }) => {
                    const graphOptionKey = graphRefToString({
                      graphId: graph.id,
                      graphVariant: variant.name,
                    });
                    const graphOptionData = keyedVariants[graphOptionKey];

                    if (!graphOptionData) {
                      throw new Error('expected option data to exist');
                    }

                    let linkToLocation: LocationDescriptor;
                    // By default we will just replace the graphId and graphVariant in the URL when switching variants.
                    // Some routes, like those with ids, need to be patched in some way to transition correctly.
                    //
                    // TODO(tim): invent a feature of RouteConfig that allows us to
                    // define transitions declaratively as a feature of a RouteConfig,
                    // like a `patchForTransition` callback that declares how to
                    // perform specific transitions.
                    if (
                      matchPath(
                        location.pathname,
                        launchesRouteConfig.definition,
                      )
                    ) {
                      // when transitioning variants on the launches route,
                      // delete the launchId from the URL.
                      linkToLocation = launchesRouteConfig.locationFrom({
                        location,
                        patch: {
                          graphId: graphOptionData.graph.id,
                          graphVariant: graphOptionData.variant.name,
                          launchId: RouteConfig.DELETE,
                        },
                      });
                    } else if (
                      matchPath(location.pathname, checkRouteConfig.definition)
                    ) {
                      // When transition from a check workflow route, go to the list instead
                      linkToLocation = recentChecksRouteConfig.location({
                        graphId: graphOptionData.graph.id,
                        graphVariant: graphOptionData.variant.name,
                        view: 'variant',
                      });
                    } else if (
                      matchPath(
                        location.pathname,
                        variantProposalListRouteConfig.definition,
                      )
                    ) {
                      // when transitioning variants on the proposals route,
                      // delete the filters from the URL / revert to default filters.
                      linkToLocation =
                        variantProposalListRouteConfig.locationFrom({
                          location,
                          patch: {
                            graphId: graphOptionData.graph.id,
                            graphVariant: graphOptionData.variant.name,
                            filterByStatuses: [
                              GraphQLTypes.ProposalStatus.OPEN,
                              GraphQLTypes.ProposalStatus.DRAFT,
                              GraphQLTypes.ProposalStatus.APPROVED,
                            ],
                            filterBySubgraphs: RouteConfig.DELETE,
                            filterByVariants: RouteConfig.DELETE,
                          },
                        });
                    } else if (
                      matchPath(
                        location.pathname,
                        variantPersistedQueriesRouteConfig.definition,
                      )
                    ) {
                      // when transitioning variants on the proposals route,
                      // delete all the filters from the URL and revert to the default state.
                      linkToLocation =
                        variantPersistedQueriesRouteConfig.locationFrom({
                          location,
                          patch: {
                            graphId: graphOptionData.graph.id,
                            graphVariant: graphOptionData.variant.name,
                            selectedOperation: RouteConfig.DELETE,
                            search: RouteConfig.DELETE,
                            clientName: RouteConfig.DELETE,
                            clientNames: RouteConfig.DELETE,
                            clientNamesGroupSelection: RouteConfig.DELETE,
                            lastPublishedDate: RouteConfig.DELETE,
                            lastPublishedDateGroupSelection: RouteConfig.DELETE,
                            lastPublishedOption: RouteConfig.DELETE,
                            lastPublishedType: RouteConfig.DELETE,
                            operationName: RouteConfig.DELETE,
                            operationNames: RouteConfig.DELETE,
                            operationNamesGroupSelection: RouteConfig.DELETE,
                            sortColumn: RouteConfig.DELETE,
                            sortDirection: RouteConfig.DELETE,
                          },
                        });
                    } else {
                      // TODO (jason) this is brittle, but we can use it for now
                      const newPathname = location.pathname.replace(
                        `/${graphVisibilityType}/${
                          currentGraphRef.graphId
                        }/variant/${encodeURIComponent(
                          currentGraphRef.graphVariant,
                        )}`,
                        `/${graphVisibilityType}/${
                          graphOptionData.graph.id
                        }/variant/${encodeURIComponent(
                          graphOptionData.variant.name,
                        )}`,
                      );
                      linkToLocation = {
                        ...location,
                        pathname: newPathname,
                      };
                    }

                    const menuItemGraphRef = `${graphOptionData.graph.id}@${graphOptionData.variant.name}`;
                    return (
                      <MenuItem
                        description={
                          graphOptionData.variant.isFavoriteOfCurrentUser
                            ? graphOptionData.graph.title
                            : undefined
                        }
                        as={Link}
                        to={linkToLocation}
                        key={menuItemGraphRef}
                        className={classnames(
                          graphRefValue === menuItemGraphRef &&
                            'bg-btn-secondary-selected',
                        )}
                        rightIcon={
                          graphOptionData.variant.permissions
                            .canQueryBuildConfig &&
                          graphOptionData.variant.isContract ? (
                            <Badge
                              variant="neutralOutlined"
                              className="flex-shrink-0"
                            >
                              Contract
                            </Badge>
                          ) : undefined
                        }
                      >
                        <span
                          className="flex-1 truncate"
                          title={`${graphOptionData.graph.title} / ${graphOptionData.variant.name}`}
                        >
                          {graphOptionData.variant.name}
                        </span>
                      </MenuItem>
                    );
                  })}
                </MenuGroup>
                {idx < variantOptions.length - 1 ? <MenuDivider /> : null}
              </React.Fragment>
            );
          })}
        </MenuList>
      </MenuContent>
    </Menu>
  ) : null;
};
